import { FC, useMemo } from "react";
import { Button } from "@/components/button";
import { ColumnDef } from "@tanstack/react-table";
import { SessionSummarySchema } from "@/domains/openapi/types";
import { Table } from "@/components/table/table";
import { UuidFieldRow } from "@/components/table/uuid-field-row";
import { TextFieldRow } from "@/components/table/text-field-row";
import { DateTimeFieldRow } from "@/components/table/datetime-field-row";
import { DateTime } from "luxon";
import { orderBy } from "lodash-es";

interface SessionsTableProps {
  data: SessionSummarySchema[];
  onViewSession: (id: string) => void;
  pageSize: number;
}

export const SessionsTable: FC<SessionsTableProps> = ({ data, onViewSession, pageSize }) => {
  const columns = useMemo<ColumnDef<SessionSummarySchema>[]>(
    () => [
      {
        header: "Session ID",
        cell: ({ row }) => <UuidFieldRow id={row.original.id} />,
      },
      {
        header: "Space Account ID",
        cell: ({ row }) => <UuidFieldRow id={row.original.owned_by_space_account_id} />,
      },
      {
        header: "Title",
        cell: ({ row }) => <TextFieldRow value={row.original.title} />,
      },
      {
        header: "Kind",
        cell: ({ row }) => <TextFieldRow value={row.original.kind} />,
      },
      {
        header: "Status",
        cell: ({ row }) => <TextFieldRow value={row.original.status} />,
      },
      {
        header: "Created At",
        cell: ({ row }) => <DateTimeFieldRow dateStr={row.original.internal_created_at} />,
      },
      {
        header: "Updated At",
        cell: ({ row }) => <DateTimeFieldRow dateStr={row.original.internal_updated_at} />,
      },
      {
        header: "View",
        cell: ({ row }) => (
          <Button size="1" onClick={() => onViewSession(row.original.id)}>
            View
          </Button>
        ),
      },
    ],
    [onViewSession]
  );

  const sortedData = useMemo(() => {
    return orderBy(
      data,
      x => (x.internal_created_at ? DateTime.fromISO(x.internal_created_at).toMillis() : -Infinity),
      "desc"
    );
  }, [data]);

  return <Table data={sortedData} columns={columns} pageSize={pageSize} />;
};
